"use client";

import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";

import { splitNativeProps, type NativeMetadataProps } from "./apple-native";
import { cn } from "./utils";

const RadixPopover = PopoverPrimitive as any;
const hasRadixPopover =
  typeof (RadixPopover as any)?.Root !== "undefined" &&
  typeof (RadixPopover as any)?.Trigger !== "undefined" &&
  typeof (RadixPopover as any)?.Content !== "undefined";

let hasWarnedMissingRadixPopover = false;
const warnMissingRadixPopover = () => {
  if (hasWarnedMissingRadixPopover) return;
  hasWarnedMissingRadixPopover = true;
  console.warn(
    "[ui/popover] Radix Popover primitives unavailable. Falling back to basic markup.",
  );
};

const filterGeneratedProps = (props: any) => {
  const clean: any = {};
  for (const key in props) {
    if (!key.startsWith("_fg")) {
      clean[key] = props[key];
    }
  }
  return clean;
};

// Helper to clean children elements of Generated props.
// Returns the original structure — single element stays a single element
// (not a one-item array) so Radix asChild / Slot's React.Children.only check passes.
const cleanChildProps = (children: React.ReactNode): React.ReactNode => {
  if (!children) return children;

  const mapped = React.Children.map(children, (child) => {
    if (!React.isValidElement(child)) return child;
    const cleanProps = filterGeneratedProps(child.props);
    return React.cloneElement(child as React.ReactElement<any>, cleanProps);
  })!;

  return mapped.length === 1 ? mapped[0] : mapped;
};

function Popover(
  props: React.ComponentProps<typeof PopoverPrimitive.Root>
) {
  const cleanProps = filterGeneratedProps(props);
  if (!hasRadixPopover) {
    warnMissingRadixPopover();
    const { open, defaultOpen, onOpenChange, modal, ...rest } = cleanProps as any;
    return <div data-slot="popover-fallback" {...rest} />;
  }
  return <RadixPopover.Root data-slot="popover" {...cleanProps} />;
}

function PopoverTrigger({
  children,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Trigger>) {
  const cleanProps = filterGeneratedProps(props);
  const cleanChildren = cleanChildProps(children);
  
  if (!hasRadixPopover || typeof (RadixPopover as any)?.Trigger === "undefined") {
    warnMissingRadixPopover();
    const { asChild, ...rest } = cleanProps as any;
    return <div data-slot="popover-trigger-fallback" {...rest}>{cleanChildren}</div>;
  }
  
  return (
    <RadixPopover.Trigger 
      data-slot="popover-trigger" 
      {...cleanProps}
    >
      {cleanChildren}
    </RadixPopover.Trigger>
  );
}

type PopoverContentProps = React.ComponentProps<typeof PopoverPrimitive.Content> &
  NativeMetadataProps & {
    /**
     * Controls the popover's surface material.
     * - `"vibrant"` (default): the `qs-vibrant-fill` recipe — a
     *   3-layer translucent material (color-dodge `#262626` + 67 %-opaque
     *   white overlay + 50 px backdrop-blur) tuned for popovers that sit
     *   over a calm canvas, e.g. dropdown / context menus.
     * - `"solid"`: the canonical Apple macOS 26 popover material — a
     *   `var(--popover)` fill (already 92 % opaque white in light /
     *   94 % near-black in dark) layered over a `blur(20px) saturate(180%)`
     *   backdrop filter, matching the glass pill recipe used by header
     *   buttons. Apple's popovers are *not* opaque cards — they're
     *   translucent surfaces that pick up the canvas tone behind them
     *   via backdrop-blur, which gives the iPadOS / macOS frosted-glass
     *   feel without the muddy colour-dodge artifact `qs-vibrant-fill`
     *   produces over busy content. Use this for popovers over the
     *   project list, chat window, or any dense surface.
     */
    surface?: "vibrant" | "solid";
  };

function PopoverContent({
  className,
  align = "center",
  sideOffset = 4,
  surface = "vibrant",
  ...props
}: PopoverContentProps) {
  const cleanProps = filterGeneratedProps(props);
  const { nativeProps, rest } = splitNativeProps(cleanProps);
  if (!hasRadixPopover || typeof (RadixPopover as any)?.Content === "undefined") {
    warnMissingRadixPopover();
    return null;
  }
  return (
    <RadixPopover.Portal>
      <RadixPopover.Content
        data-slot="popover-content"
        data-popover-surface={surface}
        data-native-role={nativeProps["data-native-role"] ?? "popover"}
        data-native-presentation={nativeProps["data-native-presentation"] ?? "popover"}
        align={align}
        sideOffset={sideOffset}
        className={cn(
          // Surface base. Vibrant uses the `qs-vibrant-fill` recipe;
          // solid skips it entirely so `qs-vibrant-fill`'s
          // `background-color: transparent !important` never enters
          // the cascade for this popover.
          //
          // For solid we ALSO apply `backdrop-blur-[20px]` +
          // `backdrop-saturate-180`, the same recipe the global header
          // glass pills use. Apple popovers are translucent surfaces
          // that pick up the canvas tone via backdrop-blur — without
          // this the popover reads as a flat opaque card, which the
          // Apple HIG explicitly rejects ("popovers are vibrant"). The
          // `var(--popover)` token is already 92 % opaque so the
          // blurred backdrop shows through subtly.
          surface === "vibrant"
            ? "qs-vibrant-fill"
            : "bg-popover backdrop-blur-[20px] backdrop-saturate-[180%]",
          // `pointer-events-auto` defensively guarantees the popover
          // surface catches its own clicks. Without this explicit
          // declaration, the popover *should* still catch events
          // (CSS default), but on floating-header views the parent
          // shell carries `pointer-events: none` so child surfaces
          // can pass scroll/click through to the page. Even though
          // Radix portals the content to `<body>` (so it shouldn't
          // inherit the shell's `none`), being explicit here prevents
          // any future regression where someone wraps the popover
          // inside a `pointer-events-none` container by mistake. This
          // is what makes "click on the card → handled by popover,
          // not the project row visible behind it" actually work.
          "pointer-events-auto",
          "text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-auto origin-(--radix-popover-content-transform-origin) p-[1rem] outline-hidden",
          // Apple macOS 26 popover spec — 13 px outer radius. Vibrant
          // gets the canonical dual-stack `--popover-shadow` only;
          // solid additionally layers the `--glass-inset` highlight on
          // top of that, the same `inset 0 1px 0 rgba(255,255,255,0.45)`
          // top-inner bevel the global header glass pills use. The
          // inset turns the popover from a flat translucent card into
          // a *raised* surface — light reflecting off the top edge —
          // which is what gives the buttons their 3D feel and is
          // what's missing without it. Outer drop shadow + inner top
          // highlight together = Apple's "lifted glass" recipe.
          "rounded-[var(--ui-popover-radius)]",
          surface === "vibrant"
            ? "shadow-[var(--ui-popover-shadow)]"
            : "shadow-[var(--ui-popover-shadow),var(--glass-inset)]",
          className,
        )}
        onOpenAutoFocus={(e: Event) => {
          e.preventDefault();
        }}
        {...nativeProps}
        {...rest}
      />
    </RadixPopover.Portal>
  );
}

function PopoverAnchor({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Anchor>) {
  const cleanProps = filterGeneratedProps(props);
  if (!hasRadixPopover || typeof (RadixPopover as any)?.Anchor === "undefined") {
    warnMissingRadixPopover();
    const { ...rest } = cleanProps as any;
    return <div data-slot="popover-anchor-fallback" {...rest} />;
  }
  return <RadixPopover.Anchor data-slot="popover-anchor" {...cleanProps} />;
}

function PopoverArrow({
  className,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Arrow>) {
  const cleanProps = filterGeneratedProps(props);
  if (!hasRadixPopover || typeof (RadixPopover as any)?.Arrow === "undefined") {
    warnMissingRadixPopover();
    return null;
  }
  return (
    <RadixPopover.Arrow
      data-slot="popover-arrow"
      className={cn(
        "fill-background drop-shadow-[0_1px_1px_rgba(15,23,42,0.08)]",
        className,
      )}
      {...cleanProps}
    />
  );
}

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor, PopoverArrow };
